/**
 * Componente Pago Unificado — pantalla centralizada para los 5 tipos de registro de pago.
 * El usuario selecciona el tipo de pago en un dropdown y la pantalla se adapta:
 * selector de entidad, columnas, origin/destination, payload del POST.
 * Usa exclusivamente el endpoint PD (getInvoicesPD).
 * Convive con las pantallas existentes sin modificarlas.
 * Fecha: 01/10/2026
 */
import { Component, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import {
  PagosService, OptionsService, FundersService,
  PaymentDetailsService, PAYMENT_DETAIL_CONFIG, InvoicePD, TipoRegistroPago
} from '../../../services/service.index';
import { AltaSolicitudesService } from '../../../services/altasolicitudes/altasolicitudes.service';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import swal2 from 'sweetalert2';

/**
 * Interfaz que define la configuración de cada tipo de pago.
 * Controla el comportamiento dinámico de la pantalla unificada.
 * Fecha: 01/10/2026
 */
interface TipoPagoConfig {
  label: string;
  paymentSource: string;
  tipoRegistroPago: TipoRegistroPago;
  pdConfig: { flag: string; tipoRegistroPago: TipoRegistroPago };
  selectorType: 'proveedor' | 'cadena' | 'fondeador';
  originField: string;
  destinationField: string;
  emailField: string;
  showRecursoRadio: boolean;
  useFechaConsulta: boolean;
  useMoratorios: boolean;
  hasInterestCompanyLogic: boolean;
  bankAtRootLevel: boolean;
  totalFromTotalAPagar: boolean;
  originLabel: string;
  destinationLabel: string;
  showBank: boolean;
  /** [01/10/2026] True para tipos de fondeo que requieren dos selectores (fondeador + cadena). */
  dualSelector: boolean;
  /** [01/10/2026] Primer dropdown en modo dual: 'fondeador' o 'cadena'. */
  primarySelector?: 'fondeador' | 'cadena';
  /** [01/10/2026] Segundo dropdown en modo dual: 'fondeador' o 'cadena'. */
  secondarySelector?: 'fondeador' | 'cadena';
  /** [01/10/2026] Modo de carga de bancos: 'always' carga siempre, 'conditional' solo si ACCOUNTING activo, 'never' sin bancos. */
  bankLoadMode: 'always' | 'conditional' | 'never';
  /** [01/10/2026] True para mostrar el radio de Rebate (solo COBRANZA FONDEO). */
  showRebateRadio: boolean;
}

/**
 * Mapa de configuración por tipo de pago.
 * Cada entrada define el comportamiento completo del formulario para ese tipo.
 * Fecha: 01/10/2026
 */
const TIPO_PAGO_MAP: { [key: string]: TipoPagoConfig } = {
  PAGO_ANTICIPADO: {
    label: 'PAGO ANTICIPADO',
    paymentSource: 'PROVEEDOR',
    tipoRegistroPago: 'PAGO ANTICIPADO',
    pdConfig: PAYMENT_DETAIL_CONFIG.PAGO_PROVEEDOR,
    selectorType: 'proveedor',
    originField: 'id_cont_finan',
    destinationField: 'id_contribuyente',
    emailField: 'email_contribuyente',
    showRecursoRadio: true,
    useFechaConsulta: false,
    useMoratorios: false,
    hasInterestCompanyLogic: true,
    bankAtRootLevel: false,
    totalFromTotalAPagar: true,
    originLabel: 'De Financiera',
    destinationLabel: 'A Proveedor',
    showBank: true,
    dualSelector: false,
    bankLoadMode: 'always',
    showRebateRadio: false
  },
  COBRANZA_FACTORAJE: {
    label: 'COBRANZA FACTORAJE',
    paymentSource: 'CADENA',
    tipoRegistroPago: 'COBRANZA FACTORAJE',
    pdConfig: PAYMENT_DETAIL_CONFIG.COBRANZA_CADENA,
    selectorType: 'cadena',
    originField: 'id_contribuyente',
    destinationField: 'id_cont_finan',
    emailField: 'email_contribuyente',
    showRecursoRadio: false,
    useFechaConsulta: true,
    useMoratorios: true,
    hasInterestCompanyLogic: false,
    bankAtRootLevel: false,
    totalFromTotalAPagar: true,
    originLabel: 'De Cadena',
    destinationLabel: 'A Financiera',
    showBank: true,
    dualSelector: false,
    bankLoadMode: 'always',
    showRebateRadio: false
  },
  PAGO_ANTICIPADO_FONDEO: {
    label: 'PAGO ANTICIPADO FONDEO',
    paymentSource: 'FONDEO',
    tipoRegistroPago: 'PAGO ANTICIPADO FONDEO',
    pdConfig: PAYMENT_DETAIL_CONFIG.PAGO_FONDEO,
    selectorType: 'fondeador',
    originField: 'id_contribuyente',
    destinationField: 'id_cont_finan',
    emailField: 'email_finan',
    showRecursoRadio: false,
    useFechaConsulta: false,
    useMoratorios: false,
    hasInterestCompanyLogic: false,
    bankAtRootLevel: true,
    totalFromTotalAPagar: true,
    originLabel: 'De Fondeador',
    destinationLabel: 'A Financiera',
    showBank: true,
    dualSelector: true,
    primarySelector: 'fondeador',
    secondarySelector: 'cadena',
    bankLoadMode: 'conditional',
    showRebateRadio: false
  },
  COBRANZA_FONDEO: {
    label: 'COBRANZA FONDEO',
    paymentSource: 'PAGO_FONDEO',
    tipoRegistroPago: 'COBRANZA FONDEO',
    pdConfig: PAYMENT_DETAIL_CONFIG.COBRANZA_FONDEO,
    selectorType: 'fondeador',
    originField: 'id_cont_finan',
    destinationField: 'id_contribuyente',
    emailField: 'email_contribuyente',
    showRecursoRadio: false,
    useFechaConsulta: false,
    useMoratorios: false,
    hasInterestCompanyLogic: false,
    bankAtRootLevel: false,
    totalFromTotalAPagar: false,
    originLabel: 'De Financiera',
    destinationLabel: 'A Fondeador',
    showBank: false,
    dualSelector: true,
    primarySelector: 'cadena',
    secondarySelector: 'fondeador',
    bankLoadMode: 'never',
    showRebateRadio: true
  },
  PAGO_RESERVA: {
    label: 'PAGO RESERVA',
    paymentSource: 'RESERVA',
    tipoRegistroPago: 'PAGO RESERVA',
    pdConfig: PAYMENT_DETAIL_CONFIG.PAGO_RESERVA,
    selectorType: 'cadena',
    originField: 'id_cont_finan',
    destinationField: 'id_contribuyente',
    emailField: 'email_contribuyente',
    showRecursoRadio: false,
    useFechaConsulta: false,
    useMoratorios: false,
    hasInterestCompanyLogic: false,
    bankAtRootLevel: false,
    totalFromTotalAPagar: false,
    originLabel: 'De Financiera',
    destinationLabel: 'A Cadena',
    showBank: true,
    dualSelector: false,
    bankLoadMode: 'always',
    showRebateRadio: false
  }
};

@Component({
  selector: 'app-pago-unificado',
  templateUrl: './pago-unificado.component.html',
  styleUrls: ['../../alta solicitudes/altasolicitudes.component.css']
})
export class PagoUnificadoComponent implements OnInit {
  @ViewChild('dt', { static: false }) tabla: Table;

  // Paginación y selección (patrón aproveedor)
  filasPorPagina: number = 100;
  opcionesTamanoPagina: number[] = [20, 50, 100];
  paginaActualSeleccionada: boolean = false;
  paginaActualIndeterminada: boolean = false;
  todasSeleccionadas: boolean = false;

  // Dropdown tipo de pago
  tiposPago = [
    { key: 'PAGO_ANTICIPADO', label: 'Pago de Financiera a Proveedor (PAGO ANTICIPADO)' },
    { key: 'COBRANZA_FACTORAJE', label: 'Pago de Cadena a Financiera (COBRANZA FACTORAJE)' },
    { key: 'PAGO_ANTICIPADO_FONDEO', label: 'Pago de Fondeador a Financiera (PAGO ANTICIPADO FONDEO)' },
    { key: 'COBRANZA_FONDEO', label: 'Pago de Financiera a Fondeador (COBRANZA FONDEO)' },
    { key: 'PAGO_RESERVA', label: 'Pago Reserva de Financiera a Empresa (PAGO RESERVA)' }
  ];
  selectedTipoPago: string = '';
  activeConfig: TipoPagoConfig = null;

  // Recurso (solo PAGO ANTICIPADO)
  valorRecurso: string = 'SIN_RECURSO';

  // Entidades (selector simple)
  entidades: any[] = [];
  selectedEntityId: string = '';
  correo: string = '';
  originId: string = '';
  destinationId: string = '';
  nomname: string = '';

  // [01/10/2026] Selectores duales para tipos de fondeo (fondeador + cadena)
  fondeadores: any[] = [];
  cadenasDual: any[] = [];
  selectedFondeadorId: string = '';
  selectedCadenaId: string = '';

  // [01/10/2026] Rebate placeholder (solo COBRANZA FONDEO)
  modoRebate: boolean = false;

  // Formulario
  currency: any[] = [];
  paymentTypes: any[] = [];
  selectedBank: string = '';
  banks: any[] = [];
  hayBancos: boolean = false;
  npago: string = '';
  fecha: string = '';

  // [01/10/2026] Almacenamiento interno de bancos y flag de accounting para bankLoadMode
  private _allBanks: any[] = [];
  private _accountingActive: boolean = false;

  // Tabla
  cols: any[] = [];
  facturas: any[] = [];
  selectedFac: any[] = [];
  totalFacs: string = '0';
  totalFacsFormateado: string = '0';

  constructor(
    public _pagosservice: PagosService,
    public _optionsservice: OptionsService,
    private _solicitudesservice: AltaSolicitudesService,
    private _paymentDetailsService: PaymentDetailsService,
    private _fundersservice: FundersService
  ) {}

  ngOnInit() {
    swal2.fire({
      title: 'Cargando',
      allowOutsideClick: false
    });
    swal2.showLoading();

    // Calcular fecha actual con ajuste de timezone (patrón estándar)
    const a = new Date();
    a.setMinutes(a.getMinutes() + a.getTimezoneOffset());
    let montha = '' + (a.getMonth() + 1);
    let daya = '' + a.getDate();
    const yeara = a.getFullYear();
    if (montha.length < 2) { montha = '0' + montha; }
    if (daya.length < 2) { daya = '0' + daya; }
    this.fecha = [yeara, montha, daya].join('-');

    // Reiniciar estado
    this.totalFacs = '0';
    this.totalFacsFormateado = '0';
    this.selectedFac = [];
    this.facturas = [];
    this.correo = '';
    this.selectedEntityId = '';
    this.originId = '';
    this.destinationId = '';
    this.entidades = [];
    this.selectedTipoPago = '';
    this.activeConfig = null;
    this.fondeadores = [];
    this.cadenasDual = [];
    this.selectedFondeadorId = '';
    this.selectedCadenaId = '';
    this.modoRebate = false;
    this.selectedBank = '';
    this.banks = [];
    this.hayBancos = false;
    this._allBanks = [];
    this._accountingActive = false;

    // Cargar catálogos comunes
    this._solicitudesservice.getPaymentCurrency().subscribe(resp => this.currency = resp);
    this._solicitudesservice.getPaymentTypes().subscribe(resp => this.paymentTypes = resp);

    // [01/10/2026] Carga de bancos siempre (para bankLoadMode 'always') y flags ACCOUNTING (para 'conditional').
    // Se guardan en variables privadas; onTipoPagoChange decide qué mostrar según bankLoadMode del tipo seleccionado.
    this._pagosservice.getAcountigAcountBanks().subscribe(banksResp => {
      this._allBanks = banksResp || [];
    });
    forkJoin([
      this._solicitudesservice.getGeneralParam('ACCOUNTING_GENERATION').pipe(catchError(() => of(null))),
      this._solicitudesservice.getGeneralParam('ACCOUNTING_SPECIFIC_ACCOUNTS_MODE').pipe(catchError(() => of(null)))
    ]).subscribe(([accGeneration, accSpecificMode]: [any, any]) => {
      const generacionActiva = accGeneration && accGeneration['value'] === 'SI';
      const modoEspecifico = accSpecificMode && accSpecificMode['value'] === 'SI';
      this._accountingActive = generacionActiva && modoEspecifico;
    });

    this._solicitudesservice.getGeneralParam('NOMBRE_FINANCIERA').subscribe(resp => {
      this.nomname = resp['value'];
      swal2.close();
    });
  }

  /**
   * [01/10/2026] Getter que resuelve el campo ID de la entidad según el tipo de selector y recurso.
   * Proveedor SIN_RECURSO: id_proveedor, CON_RECURSO: id_cadena, Cadena: id_cadena, Fondeador: id_funder.
   */
  get entityIdField(): string {
    if (!this.activeConfig) { return 'id'; }
    if (this.activeConfig.selectorType === 'proveedor') {
      return this.valorRecurso === 'CON_RECURSO' ? 'id_cadena' : 'id_proveedor';
    }
    if (this.activeConfig.selectorType === 'cadena') { return 'id_cadena'; }
    if (this.activeConfig.selectorType === 'fondeador') { return 'id_funder'; }
    return 'id';
  }

  /**
   * [01/10/2026] Getter que resuelve el campo nombre de la entidad según el tipo de selector y recurso.
   * Proveedor SIN_RECURSO: nombre_proveedor, CON_RECURSO: nombre_cadena, Cadena: nombre_cadena, Fondeador: nombre_fondeador.
   */
  get entityNameField(): string {
    if (!this.activeConfig) { return 'nombre'; }
    if (this.activeConfig.selectorType === 'proveedor') {
      return this.valorRecurso === 'CON_RECURSO' ? 'nombre_cadena' : 'nombre_proveedor';
    }
    if (this.activeConfig.selectorType === 'cadena') { return 'nombre_cadena'; }
    if (this.activeConfig.selectorType === 'fondeador') { return 'nombre_fondeador'; }
    return 'nombre';
  }

  /**
   * [01/10/2026] Getter que resuelve el label del selector de entidad según el tipo de pago activo.
   */
  get entitySelectorLabel(): string {
    if (!this.activeConfig) { return 'Entidad'; }
    if (this.activeConfig.selectorType === 'proveedor') {
      return this.valorRecurso === 'CON_RECURSO' ? 'Empresa' : 'Proveedor';
    }
    if (this.activeConfig.selectorType === 'cadena') { return 'Cadena'; }
    if (this.activeConfig.selectorType === 'fondeador') { return 'Fondeador'; }
    return 'Entidad';
  }

  /**
   * [01/10/2026] Label del primer selector en modo dual.
   */
  get primarySelectorLabel(): string {
    if (!this.activeConfig || !this.activeConfig.primarySelector) { return ''; }
    return this.activeConfig.primarySelector === 'fondeador' ? 'Fondeador' : 'Empresa';
  }

  /**
   * [01/10/2026] Label del segundo selector en modo dual.
   */
  get secondarySelectorLabel(): string {
    if (!this.activeConfig || !this.activeConfig.secondarySelector) { return ''; }
    return this.activeConfig.secondarySelector === 'fondeador' ? 'Fondeador' : 'Empresa';
  }

  /**
   * [01/10/2026] Maneja el cambio de tipo de pago en el dropdown.
   * Establece la configuración activa, limpia el estado, configura bancos según bankLoadMode
   * y carga las entidades correspondientes.
   */
  onTipoPagoChange(key: string) {
    if (!key) {
      this.activeConfig = null;
      return;
    }
    this.activeConfig = TIPO_PAGO_MAP[key];
    // Limpiar estado
    this.entidades = [];
    this.facturas = [];
    this.selectedFac = [];
    this.correo = '';
    this.selectedEntityId = '';
    this.originId = '';
    this.destinationId = '';
    this.totalFacsFormateado = '0';
    this.totalFacs = '0';
    this.valorRecurso = 'SIN_RECURSO';
    // [01/10/2026] Limpiar estado de selectores duales
    this.fondeadores = [];
    this.cadenasDual = [];
    this.selectedFondeadorId = '';
    this.selectedCadenaId = '';
    this.modoRebate = false;
    this.selectedBank = '';

    // [01/10/2026] Configurar bancos según bankLoadMode del tipo seleccionado
    if (this.activeConfig.bankLoadMode === 'always') {
      this.banks = this._allBanks;
      this.hayBancos = this._allBanks.length > 0;
    } else if (this.activeConfig.bankLoadMode === 'conditional') {
      this.banks = this._accountingActive ? this._allBanks : [];
      this.hayBancos = this._accountingActive && this._allBanks.length > 0;
    } else { // 'never'
      this.banks = [];
      this.hayBancos = false;
    }

    // [05/10/2026] Columnas PD según imagen: 12 base + moratorios extra para COBRANZA FACTORAJE
    if (this.activeConfig.useMoratorios) {
      this.cols = [
        { field: 'folio_reporte', header: 'Folio Reporte' },
        { field: 'folio_factura', header: 'Folio Factura' },
        { field: 'uuid', header: 'UUID' },
        { field: 'total_a_pagar', header: 'Total a Pagar' },
        { field: 'emisor', header: 'Emisor' },
        { field: 'receptor', header: 'Receptor' },
        { field: 'moneda', header: 'Moneda' },
        { field: 'fecha_operacion', header: 'Fecha Operación' },
        { field: 'fecha_vencimiento', header: 'Fecha Vencimiento' },
        { field: 'fecha_emision', header: 'Fecha Emisión' },
        { field: 'fecha_carga', header: 'Fecha Carga' },
        { field: 'estatus', header: 'Estatus' },
        { field: 'moratorios_fmt', header: 'Moratorios' },
        { field: 'dias_vencidos', header: 'Dias Vencidos' },
        { field: 'saldo_pendiente_fmt', header: 'Saldo Pendiente' }
      ];
    } else {
      this.cols = [
        { field: 'folio_reporte', header: 'Folio Reporte' },
        { field: 'folio_factura', header: 'Folio Factura' },
        { field: 'uuid', header: 'UUID' },
        { field: 'total_a_pagar', header: 'Total a Pagar' },
        { field: 'emisor', header: 'Emisor' },
        { field: 'receptor', header: 'Receptor' },
        { field: 'moneda', header: 'Moneda' },
        { field: 'fecha_operacion', header: 'Fecha Operación' },
        { field: 'fecha_vencimiento', header: 'Fecha Vencimiento' },
        { field: 'fecha_emision', header: 'Fecha Emisión' },
        { field: 'fecha_carga', header: 'Fecha Carga' },
        { field: 'estatus', header: 'Estatus' }
      ];
    }

    // Cargar entidades según tipo de selector
    this.cargarEntidades();
  }

  /**
   * [01/10/2026] Maneja el cambio de radio SIN/CON recurso (solo PAGO ANTICIPADO).
   * Recarga las entidades según el recurso seleccionado.
   */
  onRecursoChange(value: string) {
    this.valorRecurso = value;
    this.entidades = [];
    this.facturas = [];
    this.selectedFac = [];
    this.correo = '';
    this.selectedEntityId = '';
    this.originId = '';
    this.destinationId = '';
    this.totalFacsFormateado = '0';
    this.totalFacs = '0';
    this.cargarEntidades();
  }

  /**
   * [01/10/2026] Maneja el cambio del radio Rebate (solo COBRANZA FONDEO).
   * En pantalla unificada (modo PD), el rebate no está implementado. Se muestra un aviso al usuario.
   */
  onRebateChange(esRebate: boolean) {
    this.modoRebate = esRebate;
    if (esRebate) {
      swal2.fire('Modo rebate no disponible en pantalla unificada', '', 'info');
      this.modoRebate = false;
    }
  }

  /**
   * [01/10/2026] Carga la lista de entidades según el tipo de selector y recurso activo.
   * Para tipos con dualSelector: carga fondeadores y cadenas en paralelo con forkJoin.
   * Para selector simple: Proveedor → getProveedores()/getpayment_with_recourse_companies(),
   * Cadena → getCadenas(), Fondeador → getfundersfinancial().
   */
  private cargarEntidades() {
    if (!this.activeConfig) { return; }
    swal2.fire({ title: 'Cargando', allowOutsideClick: false });
    swal2.showLoading();

    // [01/10/2026] Selectores duales: cargar fondeadores y cadenas en paralelo
    if (this.activeConfig.dualSelector) {
      forkJoin([
        this._fundersservice.getfundersfinancial(),
        this._pagosservice.getCadenas()
      ]).subscribe(([fondeadores, cadenas]) => {
        this.fondeadores = fondeadores;
        this.cadenasDual = cadenas;
        swal2.close();
      }, (err) => {
        swal2.close();
        console.log(err);
      });
      return;
    }

    // Selector simple (lógica existente sin cambios)
    if (this.activeConfig.selectorType === 'proveedor') {
      if (this.valorRecurso === 'SIN_RECURSO') {
        this._pagosservice.getProveedores().subscribe(resp => {
          this.entidades = resp;
          swal2.close();
        });
      } else {
        this._pagosservice.getpayment_with_recourse_companies().subscribe(resp => {
          this.entidades = resp;
          swal2.close();
        });
      }
    } else if (this.activeConfig.selectorType === 'cadena') {
      this._pagosservice.getCadenas().subscribe(resp => {
        this.entidades = resp;
        swal2.close();
      });
    } else if (this.activeConfig.selectorType === 'fondeador') {
      this._fundersservice.getfundersfinancial().subscribe(resp => {
        this.entidades = resp;
        swal2.close();
      });
    }
  }

  /**
   * [01/10/2026] Maneja la selección de una entidad en el dropdown (selector simple).
   * Extrae originId, destinationId y correo de la entidad seleccionada.
   * Si hasInterestCompanyLogic y la entidad tiene intereses_cadena, usa interest_company_email.
   */
  onEntitySelect(entityId: string) {
    this.selectedEntityId = entityId;
    this.facturas = [];
    this.selectedFac = [];
    this.totalFacsFormateado = '0';
    this.totalFacs = '0';
    this.paginaActualSeleccionada = false;
    this.paginaActualIndeterminada = false;
    this.todasSeleccionadas = false;

    if (!entityId) {
      this.originId = '';
      this.destinationId = '';
      this.correo = '';
      return;
    }

    // Buscar entidad por su ID
    const idField = this.entityIdField;
    const entity = this.entidades.find(e => e[idField] == entityId);
    if (!entity) { return; }

    this.originId = entity[this.activeConfig.originField];
    this.destinationId = entity[this.activeConfig.destinationField];

    // Lógica de intereses_cadena (patrón aproveedor)
    if (this.activeConfig.hasInterestCompanyLogic && entity.intereses_cadena) {
      this.correo = entity.interest_company_email;
    } else {
      this.correo = entity[this.activeConfig.emailField];
    }
  }

  /**
   * [01/10/2026] Maneja la selección de un fondeador en modo dual selector.
   * Extrae originId, destinationId y correo del fondeador seleccionado
   * usando originField/destinationField/emailField de la configuración activa.
   * Patrón: afinanciera.getcorreo() y afondeador.getcorreo().
   */
  onFondeadorSelect(fondeadorId: string) {
    this.selectedFondeadorId = fondeadorId;
    this.facturas = [];
    this.selectedFac = [];
    this.totalFacsFormateado = '0';
    this.totalFacs = '0';
    this.paginaActualSeleccionada = false;
    this.paginaActualIndeterminada = false;
    this.todasSeleccionadas = false;
    this.selectedBank = '';

    if (!fondeadorId) {
      this.originId = '';
      this.destinationId = '';
      this.correo = '';
      return;
    }

    const fondeador = this.fondeadores.find(f => f.id_funder == fondeadorId);
    if (!fondeador) { return; }

    this.originId = fondeador[this.activeConfig.originField];
    this.destinationId = fondeador[this.activeConfig.destinationField];
    this.correo = fondeador[this.activeConfig.emailField];
  }

  /**
   * [01/10/2026] Maneja la selección de una cadena/empresa en modo dual selector.
   * Almacena el ID de la cadena seleccionada. Los contributor IDs vienen del fondeador.
   */
  onCadenaSelect(cadenaId: string) {
    this.selectedCadenaId = cadenaId;
    this.facturas = [];
    this.selectedFac = [];
    this.totalFacsFormateado = '0';
    this.totalFacs = '0';
    this.paginaActualSeleccionada = false;
    this.paginaActualIndeterminada = false;
    this.todasSeleccionadas = false;
  }

  /**
   * [01/10/2026] Consulta facturas desde el endpoint PD.
   * Verifica el flag PD, valida selectores (simple o dual), consulta facturas y mapea al formato de tabla.
   */
  consultar() {
    // [01/10/2026] Validación de selectores según modo simple o dual
    if (this.activeConfig.dualSelector) {
      if (!this.selectedFondeadorId) {
        swal2.fire('Debe seleccionar un fondeador', '', 'error');
        return;
      }
      if (!this.selectedCadenaId) {
        swal2.fire('Debe seleccionar una empresa', '', 'error');
        return;
      }
    } else {
      if (!this.selectedEntityId) {
        swal2.fire('Debe seleccionar una entidad', '', 'error');
        return;
      }
    }

    // [01/10/2026] Validar banco si es requerido (patrón afinanciera.muestraFacturas)
    if (this.hayBancos && !this.selectedBank) {
      swal2.fire('Debe seleccionar un banco', '', 'info');
      return;
    }

    swal2.fire({ title: 'Cargando', allowOutsideClick: false });
    swal2.showLoading();

    // [01/10/2026] Consultar facturas PD directamente — la pantalla unificada solo
    // trabaja con ajustes de detalle, no tiene modo legacy. Si el backend tiene el
    // flag inactivo, el endpoint retorna 422 y el manejo de error lo cubre.
    const fechaParam = this.activeConfig.useFechaConsulta ? this.fecha : undefined;
    this._paymentDetailsService.getInvoicesPD(
      Number(this.originId),
      Number(this.destinationId),
      this.activeConfig.tipoRegistroPago,
      fechaParam
    ).subscribe(resp => {
      swal2.close();
      this.facturas = this.mapearFacturasPD(resp);
      if (this.facturas.length === 0) {
        swal2.fire('No se encontraron Datos', '', 'error');
      }
    }, (err) => {
      swal2.close();
      console.log(err);
      const titulo = (err && err.status === 422)
        ? 'Error de configuración en ajustes detalles'
        : 'Ocurrió un error al consultar ajustes detalles';
      swal2.fire({
        title: titulo,
          text: '',
          icon: 'error',
          showConfirmButton: true,
          showCancelButton: false,
          allowOutsideClick: false
        }).then(res => {
          if (res.value) { this.ngOnInit(); }
        });
      });
  }

  /**
   * [05/10/2026] Mapea facturas del endpoint PD al formato de tabla con 12 columnas base de la imagen.
   * Filtra PAGADO, formatea montos, agrega monto_para_total según totalFromTotalAPagar.
   * Incluye campos extra para moratorios cuando COBRANZA FACTORAJE está activo.
   */
  mapearFacturasPD(facturas: InvoicePD[]): any[] {
    return facturas
      .filter(f => f.payment_status !== 'PAGADO')
      .map(f => ({
        id_factura: f.invoice_id,
        folio_reporte: f.folio_reporte,
        folio_factura: f.folio_factura,
        uuid: f.uuid,
        total_a_pagar: this._paymentDetailsService.formatearMonto(f.total_a_pagar),
        emisor: f.emisor,
        receptor: f.receptor,
        moneda: f.moneda,
        fecha_operacion: f.fecha_operacion,
        fecha_vencimiento: f.fecha_vencimiento,
        fecha_emision: f.fecha_emision,
        fecha_carga: f.fecha_carga,
        estatus: f.estatus_factura,
        // [05/10/2026] Campos extra de moratorios (para variante COBRANZA FACTORAJE)
        moratorios_fmt: this._paymentDetailsService.formatearMonto(f.moratorios),
        dias_vencidos: f.dias_vencidos,
        saldo_pendiente_fmt: this._paymentDetailsService.formatearMonto(f.saldo_pendiente),
        es_parcial: f.payment_status === 'PARCIAL',
        tiene_moratorios: f.moratorios > 0,
        // Campo unificado para getTotal(): suma total_a_pagar o saldo_pendiente según config
        monto_para_total: this.activeConfig.totalFromTotalAPagar
          ? this._paymentDetailsService.formatearMonto(f.total_a_pagar)
          : this._paymentDetailsService.formatearMonto(f.saldo_pendiente)
      }));
  }

  /**
   * [01/10/2026] Calcula el total de las facturas seleccionadas.
   * Suma monto_para_total (patrón estándar con parseFloat y replace de comas).
   */
  getTotal() {
    let tp = 0;
    if (this.selectedFac.length === 0) {
      tp = 0;
    } else {
      // tslint:disable-next-line: forin
      for (const prop in this.selectedFac) {
        tp = tp + parseFloat(this.selectedFac[prop].monto_para_total.replace(/,/g, ''));
      }
    }
    this.totalFacs = tp.toFixed(2);
    this.totalFacsFormateado = tp.toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /**
   * [01/10/2026] Registra el pago unificado.
   * Construye el payload según la configuración activa y llama a aplicarPagoRAWproveedor.
   * bankAtRootLevel controla si el banco va en payment o a nivel raíz del payload.
   */
  guardar() {
    if (this.selectedFac.length === 0) {
      swal2.fire('Debe seleccionar al menos una Factura', '', 'error');
      return;
    }

    const tpago: any = document.getElementById('tdpago');
    const moneda: any = document.getElementById('moneda');
    const valortpago = tpago.options[tpago.selectedIndex].value;
    const valormoneda = moneda.options[moneda.selectedIndex].value;

    const payment: any = {
      payment_date: (document.getElementById('fecha') as HTMLInputElement).value,
      payment_number: (document.getElementById('npago') as HTMLInputElement).value,
      payment_type: valortpago,
      currency: valormoneda,
      amount: this.totalFacs,
      email_cfdi: (document.getElementById('correo') as HTMLInputElement).value,
      notes: (document.getElementById('notas') as HTMLInputElement).value,
      voucher: (document.getElementById('voucher') as HTMLInputElement).value,
      contributor_from_id: this.originId,
      contributor_to_id: this.destinationId
    };

    // Banco dentro de payment o a nivel raíz según configuración
    if (!this.activeConfig.bankAtRootLevel && this.hayBancos && this.selectedBank) {
      payment.bank = this.selectedBank;
    }

    const params: any = {
      payment_source: this.activeConfig.paymentSource,
      invoices: [],
      payment: payment
    };

    // Banco a nivel raíz del payload (patrón afinanciera FONDEO)
    if (this.activeConfig.bankAtRootLevel && this.hayBancos && this.selectedBank) {
      params.bank = this.selectedBank;
    }

    // tslint:disable-next-line: forin
    for (const prop in this.selectedFac) {
      params.invoices[prop] = { id: this.selectedFac[prop].id_factura.toString() };
    }

    swal2.fire({
      title: 'Procesando pago',
      text: 'Registrando la información...',
      allowOutsideClick: false
    });
    swal2.showLoading();

    this._pagosservice.aplicarPagoRAWproveedor(params).subscribe(resp => {
      swal2.close();
      swal2.fire({
        title: 'La información se registró',
        text: 'con éxito',
        icon: 'success',
        showConfirmButton: true,
        showCancelButton: false,
        allowOutsideClick: false
      }).then(res => {
        if (res.value) {
          window.location.reload();
        }
      });
    }, (err) => {
      swal2.close();
      console.log(err);
      swal2.fire({
        title: 'Ocurrio un error',
        text: err.error.errors[0],
        icon: 'error',
        showConfirmButton: true,
        showCancelButton: false,
        allowOutsideClick: false
      }).then(res => {
        if (res.value) {
          this.ngOnInit();
        }
      });
    });
  }

  // --- Selección con paginación (patrón aproveedor/pago-reserva) ---

  /**
   * [01/10/2026] Obtiene las filas visibles en la página actual de la tabla.
   */
  obtenerFilasPaginaActual(): any[] {
    if (!this.tabla) { return []; }
    const datos = this.tabla.filteredValue || this.tabla.value || [];
    const inicio = this.tabla.first || 0;
    const fin = inicio + (this.tabla.rows || this.filasPorPagina);
    return datos.slice(inicio, fin);
  }

  /**
   * [01/10/2026] Alterna la selección de todas las filas de la página actual.
   */
  toggleSeleccionPagina(): void {
    const filasPagina = this.obtenerFilasPaginaActual();
    const todasSel = filasPagina.length > 0 && filasPagina.every(fila => this.selectedFac.includes(fila));
    if (todasSel) {
      this.selectedFac = this.selectedFac.filter(fila => !filasPagina.includes(fila));
    } else {
      const nuevas = filasPagina.filter(fila => !this.selectedFac.includes(fila));
      this.selectedFac = [...this.selectedFac, ...nuevas];
    }
    this.actualizarEstadoCheckboxPagina();
    this.getTotal();
  }

  /**
   * [01/10/2026] Actualiza el estado visual del checkbox de selección de página.
   */
  actualizarEstadoCheckboxPagina(): void {
    const filasPagina = this.obtenerFilasPaginaActual();
    if (filasPagina.length === 0) {
      this.paginaActualSeleccionada = false;
      this.paginaActualIndeterminada = false;
      return;
    }
    const seleccionadas = filasPagina.filter(fila => this.selectedFac.includes(fila)).length;
    this.paginaActualSeleccionada = seleccionadas === filasPagina.length;
    this.paginaActualIndeterminada = seleccionadas > 0 && seleccionadas < filasPagina.length;
  }

  /**
   * [01/10/2026] Alterna la selección de todas las filas (filtradas o totales).
   */
  toggleSeleccionTodas(): void {
    const datos = (this.tabla && this.tabla.filteredValue) ? this.tabla.filteredValue : this.facturas;
    if (this.todasSeleccionadas) {
      this.selectedFac = [];
    } else {
      this.selectedFac = [...datos];
    }
    this.todasSeleccionadas = !this.todasSeleccionadas;
    this.actualizarEstadoCheckboxPagina();
    this.getTotal();
  }

  /**
   * [01/10/2026] Actualiza el estado visual del checkbox "Todas".
   */
  actualizarEstadoCheckboxTodas(): void {
    const datos = (this.tabla && this.tabla.filteredValue) ? this.tabla.filteredValue : this.facturas;
    this.todasSeleccionadas = datos.length > 0 && this.selectedFac.length === datos.length;
  }
}
